import classNames from 'classnames';
import styles from './CasinoGameList.module.css';
import { CasinoGameListItem } from 'components/CasinoGameListItem/CasinoGameListItem';
import type { PointerEvent as ReactPointerEvent } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { CasinoGame } from 'types/CasinoGame';
import { UserContext } from 'context/userContext';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { BrandLogo } from 'components/BrandLogo/BrandLogo';
import { usePlayerLayout } from 'lib/playerLayout';
import { ModernCasinoBrowser } from './ModernCasinoBrowser';
import { ClassicCasinoRail } from './ClassicCasinoRail';
import { casinoProviderLabel } from 'helpers/casinoProvider';
import { useCasinoFavorites } from 'hooks/useCasinoFavorites';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveCasinoReturn } from 'lib/casinoReturn';
import { toast } from 'react-toastify';
import { getGameUrl } from 'services/api/gameApiService';
import { ToastMessagesIDs } from 'types/enums';

interface Props {
  gameList: any;
  useLiveCasino?: boolean;
  /** A search is on (the modern lobby then shows only the matches). */
  searching?: boolean;
}

const FAB_VIEWPORT_MARGIN = 8;
/** Matches .mobileFullscreenFab right/bottom inset (safe-area is layered in CSS; snap uses min corner inset). */
const FAB_CORNER_INSET = 12;

function clampFabToViewport(
  left: number,
  top: number,
  width: number,
  height: number
) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const m = FAB_VIEWPORT_MARGIN;
  return {
    left: Math.min(Math.max(m, left), Math.max(m, vw - width - m)),
    top: Math.min(Math.max(m, top), Math.max(m, vh - height - m)),
  };
}

export const CasinoGameList = ({
  gameList,
  useLiveCasino = false,
  searching = false,
}: Props) => {
  const providers = useMemo(() => {
    return Array.isArray(gameList) ? gameList : [];
  }, [gameList]);

  const [gameUrl, setGameUrl] = useState<string>('');
  const [isGameLoading, setIsGameLoading] = useState(false);
  /** Hide blank iframe until embedded document fires load (cross-origin still fires when frame loads). */
  const [iframeReady, setIframeReady] = useState(false);
  const gameLoadSeqRef = useRef(0);
  const scrollAtTapRef = useRef(0);
  const [expanded, setExpanded] = useState<boolean[]>(() =>
    providers.map((_, idx) => idx === 0)
  );
  const currentGameRef = useRef<HTMLDivElement>(null);
  const { loggedUser } = useContext(UserContext);
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  const favs = useCasinoFavorites(useLiveCasino ? 'live' : 'casino');
  // Starred games of this lobby, newest first (a game the provider dropped
  // is left out).
  const favoriteGames = useMemo(() => {
    const byUuid = new Map<string, CasinoGame>();
    providers.forEach((p: any) =>
      p.games?.forEach((g: CasinoGame) => byUuid.set(g.uuid, g))
    );
    return favs.uuids
      .map((u) => byUuid.get(u))
      .filter((g): g is CasinoGame => !!g);
  }, [providers, favs.uuids]);
  const tile = (game: CasinoGame, lazy = false) => (
    <CasinoGameListItem
      game={game}
      key={game.uuid}
      startGameLoad={startGameLoad}
      completeGameLoad={completeGameLoad}
      failGameLoad={failGameLoad}
      useLiveCasino={useLiveCasino}
      lazy={lazy}
      favorite={favs.isFavorite(game.uuid)}
      onToggleFavorite={() => favs.toggle(game.uuid)}
    />
  );

  useEffect(() => {
    // Keep `expanded` aligned with provider count.
    setExpanded((prev) => {
      if (providers.length === prev.length) return prev;
      const next = providers.map((_, idx) => prev[idx] ?? idx === 0);
      if (next.length > 0 && next.every((v) => !v)) {
        next[0] = true;
      }
      return next;
    });
  }, [providers.length]);

  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iOS Safari / iPadOS detection (including iPad in desktop mode).
    return (
      /iPad|iPhone|iPod/i.test(ua) ||
      (navigator.platform === 'MacIntel' &&
        (navigator as any).maxTouchPoints > 1)
    );
  }, []);

  const canUseFullscreen = useMemo(() => {
    if (typeof document === 'undefined') return false;
    if (isIOS) return false; // iOS fullscreen for arbitrary elements is unreliable; hide the control.
    const docAny = document as any;
    const enabled =
      !!document.fullscreenEnabled || !!docAny.webkitFullscreenEnabled;
    const hasWebkitRequest =
      typeof HTMLElement !== 'undefined' &&
      typeof (HTMLElement.prototype as any).webkitRequestFullscreen ===
        'function';
    return enabled || hasWebkitRequest;
  }, [isIOS]);

  /** Desktop ribbon only; exit fullscreen via browser (Esc). */
  const enterGameFullscreen = useCallback(() => {
    const el = currentGameRef.current;
    if (!el) {
      return;
    }
    if (!canUseFullscreen) {
      return;
    }
    if (document.fullscreenElement === el) {
      return;
    }
    if (el.requestFullscreen) {
      const p = el.requestFullscreen();
      // Some browsers reject without user gesture; swallow to avoid breaking UI.
      if (p && typeof (p as any).catch === 'function') {
        void (p as any).catch(() => {});
      }
    } else if (
      (el as HTMLDivElement & { webkitRequestFullscreen?: () => void })
        .webkitRequestFullscreen
    ) {
      try {
        void (
          el as HTMLDivElement & { webkitRequestFullscreen: () => void }
        ).webkitRequestFullscreen();
      } catch {
        // ignore
      }
    }
  }, [canUseFullscreen]);

  const startGameLoad = useCallback(() => {
    scrollAtTapRef.current = window.scrollY;
    gameLoadSeqRef.current += 1;
    const seq = gameLoadSeqRef.current;
    /** Mount the game container in the same task as the tap so `requestFullscreen` still counts as a user gesture. */
    flushSync(() => {
      setIsGameLoading(true);
      setGameUrl('');
      setIframeReady(false);
    });
    if (isMobile && canUseFullscreen) {
      enterGameFullscreen();
    }
    return seq;
  }, [isMobile, canUseFullscreen, enterGameFullscreen]);

  const completeGameLoad = useCallback(
    (seq: number, url: string) => {
      if (seq !== gameLoadSeqRef.current) {
        return;
      }
      // Live casino on an iPhone/iPad opens as the whole page: inside our
      // frame Safari keeps the provider's tables from opening (the lobby
      // loads, a table goes black and back to the lobby; two iPhones,
      // 2026-11-01). The loading screen stays until the page changes.
      if (isIOS && useLiveCasino) {
        saveCasinoReturn(scrollAtTapRef.current);
        window.location.assign(url);
        return;
      }
      setGameUrl(url);
      setIsGameLoading(false);
    },
    [isIOS, useLiveCasino]
  );

  // The provider refused to start the game (e.g. Belatra: "This currency is
  // not enabled for your contract", 2026-11-02). Say so instead of only
  // dropping the player back on the list.
  const failGameLoad = useCallback(
    (seq: number) => {
      if (seq !== gameLoadSeqRef.current) {
        return;
      }
      setGameUrl('');
      setIsGameLoading(false);
      setIframeReady(false);
      toast(t('casino_game_unavailable'), {
        toastId: ToastMessagesIDs.CASINO_GAMES_ACCESS_ERROR,
      });
    },
    [t]
  );

  // Our own way out of a game on a phone: the game fills the screen and not
  // every game has a close button of its own (Play'n GO, 2026-11-02).
  const closeGame = useCallback(() => {
    gameLoadSeqRef.current += 1;
    setGameUrl('');
    setIsGameLoading(false);
    setIframeReady(false);
  }, []);

  useEffect(() => {
    if (!loggedUser) {
      setGameUrl('');
      setIsGameLoading(false);
      setIframeReady(false);
    }
  }, [loggedUser]);

  useEffect(() => {
    if (gameUrl && !isGameLoading) {
      window.scrollTo(0, 0);
    }

    // A game's own way out. Amatic's house button sends "GT:EXIT" to the top
    // page and waits for it to close the game: it only goes to its exit
    // address itself when it is the whole page (owner, 2026-11-03: "please
    // wait..." and nothing closed). casino-exit.html (the launch's
    // return_url) sends "closeFrame".
    const CLOSE_MESSAGES = ['closeFrame', 'close-frame', 'GT:EXIT'];
    const messageListener = (event: any) => {
      if (
        CLOSE_MESSAGES.includes(event.data) ||
        CLOSE_MESSAGES.includes(event.message)
      ) {
        setGameUrl('');
        setIsGameLoading(false);
        setIframeReady(false);
      }
    };

    window.addEventListener('message', messageListener);

    return () => window.removeEventListener('message', messageListener);
  }, [gameUrl, isGameLoading]);

  // /casino-games?play=<uuid> (the home page's top games) opens that game.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // An open game is a step in the history (?game=1): the phone's or the
  // browser's Back closes it and leaves the player on this list, instead of
  // taking them off the casino page. A game closed from inside takes its
  // step back out.
  const gameOpen = isGameLoading || !!gameUrl;
  const gameStepRef = useRef(false);
  useEffect(() => {
    const inUrl = searchParams.has('game');
    // A game that takes the whole page (iPhone) leaves this one instead.
    const ownPage = isIOS && useLiveCasino;
    if (gameOpen && !inUrl && !gameStepRef.current && !ownPage) {
      gameStepRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.set('game', '1');
      setSearchParams(next);
    } else if (gameOpen && !inUrl && gameStepRef.current && !ownPage) {
      // Back was pressed.
      gameStepRef.current = false;
      gameLoadSeqRef.current += 1;
      setGameUrl('');
      setIsGameLoading(false);
      setIframeReady(false);
    } else if (!gameOpen && inUrl) {
      if (gameStepRef.current) {
        gameStepRef.current = false;
        navigate(-1);
      } else {
        const next = new URLSearchParams(searchParams);
        next.delete('game');
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOpen, searchParams]);

  // A closed game puts the list back where the player tapped it (the game
  // opened at the top of the page).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !gameOpen) {
      const y = scrollAtTapRef.current;
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
    wasOpenRef.current = gameOpen;
  }, [gameOpen]);

  // Safari can bring this page back from its back-forward cache after a
  // game that took the whole page (iPhone): still on the loading screen.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      gameLoadSeqRef.current += 1;
      setGameUrl('');
      setIsGameLoading(false);
      setIframeReady(false);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const playUuid = useLiveCasino ? null : searchParams.get('play');
  useEffect(() => {
    if (!playUuid) return;
    const next = new URLSearchParams(searchParams);
    next.delete('play');
    setSearchParams(next, { replace: true });
    if (!loggedUser) {
      toast(t('must_be_logged_in'), {
        toastId: ToastMessagesIDs.CASINO_GAMES_ACCESS_ERROR,
      });
      return;
    }
    const seq = startGameLoad();
    getGameUrl(playUuid, loggedUser.id, loggedUser.username)
      .then((url) => completeGameLoad(seq, url))
      .catch(() => failGameLoad(seq));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playUuid]);

  const changeExpanded = (index: number) => {
    let expandedTemp = [...expanded];
    expandedTemp[index] = !expandedTemp[index];
    setExpanded(expandedTemp);
  };

  const showGame = isGameLoading || !!gameUrl;
  // Classic on a phone: the providers on a rail over one grid instead of a
  // long list of provider sections (a search still lists its matches).
  const classicRail = !modern && isMobile && !searching && !showGame;

  const mobileFabRef = useRef<HTMLButtonElement>(null);
  const [mobileFabPos, setMobileFabPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const mobileFabDragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startLeft: number;
    startTop: number;
    moved: boolean;
  } | null>(null);
  const suppressMobileFabClickRef = useRef(false);

  useEffect(() => {
    if (!showGame) {
      setMobileFabPos(null);
    }
  }, [showGame]);

  const snapMobileFabToCorner = useCallback(() => {
    const el = mobileFabRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (!w || !h) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const inset = FAB_CORNER_INSET;
    const left = vw - w - inset;
    const top = vh - h - inset;
    setMobileFabPos(clampFabToViewport(left, top, w, h));
  }, []);

  useLayoutEffect(() => {
    if (!showGame || !isMobile || !canUseFullscreen) return;
    snapMobileFabToCorner();
  }, [
    showGame,
    isMobile,
    canUseFullscreen,
    gameUrl,
    iframeReady,
    snapMobileFabToCorner,
  ]);

  useEffect(() => {
    if (!showGame || !isMobile || !canUseFullscreen) return;
    const onOrientationChange = () => {
      const run = () => snapMobileFabToCorner();
      requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
      window.addEventListener('resize', run, { once: true });
    };
    const onFullscreenChange = () => {
      const docAny = document as Document & {
        webkitFullscreenElement?: Element | null;
      };
      const fs =
        document.fullscreenElement ?? docAny.webkitFullscreenElement ?? null;
      if (!fs) {
        const run = () => snapMobileFabToCorner();
        requestAnimationFrame(() => {
          requestAnimationFrame(run);
        });
      }
    };
    window.addEventListener('orientationchange', onOrientationChange);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener(
      'webkitfullscreenchange',
      onFullscreenChange as EventListener
    );
    return () => {
      window.removeEventListener('orientationchange', onOrientationChange);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        onFullscreenChange as EventListener
      );
    };
  }, [showGame, isMobile, canUseFullscreen, snapMobileFabToCorner]);

  useEffect(() => {
    if (mobileFabPos === null) return;
    const onResize = () => {
      const el = mobileFabRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setMobileFabPos((p) => (p ? clampFabToViewport(p.left, p.top, w, h) : p));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mobileFabPos]);

  const handleMobileFabPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return;
      const el = mobileFabRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      /** Don’t setState here when `mobileFabPos` is null — a re-render between pointerdown and click can drop the first tap. */
      const left = mobileFabPos?.left ?? r.left;
      const top = mobileFabPos?.top ?? r.top;
      mobileFabDragRef.current = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startLeft: left,
        startTop: top,
        moved: false,
      };
      el.setPointerCapture(e.pointerId);
    },
    [mobileFabPos]
  );

  const handleMobileFabPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const d = mobileFabDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const dx = e.clientX - d.startClientX;
      const dy = e.clientY - d.startClientY;
      if (Math.abs(dx) + Math.abs(dy) > 6) {
        d.moved = true;
      }
      const el = mobileFabRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setMobileFabPos(
        clampFabToViewport(d.startLeft + dx, d.startTop + dy, w, h)
      );
    },
    []
  );

  const endMobileFabDrag = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const d = mobileFabDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      mobileFabRef.current?.releasePointerCapture(e.pointerId);
      if (d.moved) {
        suppressMobileFabClickRef.current = true;
      }
      mobileFabDragRef.current = null;
    },
    []
  );

  const handleMobileFabLostCapture = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const d = mobileFabDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      if (d.moved) {
        suppressMobileFabClickRef.current = true;
      }
      mobileFabDragRef.current = null;
    },
    []
  );

  const handleMobileFabClick = useCallback(() => {
    if (suppressMobileFabClickRef.current) {
      suppressMobileFabClickRef.current = false;
      return;
    }
    enterGameFullscreen();
  }, [enterGameFullscreen]);

  const gameLoadingOrBlank =
    (isGameLoading && !gameUrl) || (!!gameUrl && !iframeReady);

  const currentGameTree = (
    <div className={styles.currentGame} ref={currentGameRef}>
      {gameUrl ? (
        <iframe
          key={gameUrl}
          src={gameUrl}
          title="Current Game"
          allow="fullscreen; autoplay; encrypted-media; clipboard-write; picture-in-picture"
          allowFullScreen
          onLoad={() => setIframeReady(true)}
          className={styles.gameIframe}
        />
      ) : null}
      {gameLoadingOrBlank ? (
        <div
          className={styles.gameLoadingPlaceholder}
          aria-busy="true"
          aria-live="polite"
        >
          <div className={styles.gameLoadingShimmer} aria-hidden />
          <div className={styles.gameLoadingInner}>
            <BrandLogo
              theme={loggedUser?.player_theme}
              variant="overlay"
              className={styles.gameLoadingLogo}
            />
            <div className={styles.gameLoadingSpinner} aria-hidden />
            <p className={styles.gameLoadingText}>
              {t('game_loading', { defaultValue: 'Loading game…' })}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );

  const mobileGamePortal =
    showGame && isMobile && typeof document !== 'undefined'
      ? createPortal(
          <div className={styles.mobileGamePortalLayer}>
            {currentGameTree}
            <button
              type="button"
              className={styles.mobileCloseGame}
              onClick={closeGame}
              aria-label={t('close_game')}
              title={t('close_game')}
            >
              <span aria-hidden>×</span>
            </button>
            {canUseFullscreen ? (
              <button
                ref={mobileFabRef}
                type="button"
                className={classNames(
                  styles.mobileFullscreenFab,
                  'desktop-hidden'
                )}
                style={
                  mobileFabPos
                    ? {
                        left: mobileFabPos.left,
                        top: mobileFabPos.top,
                        right: 'auto',
                        bottom: 'auto',
                      }
                    : undefined
                }
                onPointerDown={handleMobileFabPointerDown}
                onPointerMove={handleMobileFabPointerMove}
                onPointerUp={endMobileFabDrag}
                onPointerCancel={endMobileFabDrag}
                onLostPointerCapture={handleMobileFabLostCapture}
                onClick={handleMobileFabClick}
                disabled={isGameLoading || !gameUrl || !iframeReady}
                title={t('fullscreen_fab_hint', {
                  defaultValue: 'Drag to move • Tap for fullscreen',
                })}
                aria-label={t('fullscreen_fab_aria', {
                  defaultValue: 'Fullscreen. Drag to reposition on screen.',
                })}
              >
                <span className={styles.mobileFullscreenFabGrip} aria-hidden />
                <span className={styles.mobileFullscreenFabLabel}>
                  {t('enter_fullscreen', { defaultValue: 'Fullscreen' })}
                </span>
              </button>
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {mobileGamePortal}
      <div className={styles.listContainer}>
        {showGame && !isMobile ? (
          <>
            <div
              className={classNames(
                styles.providerWrapper,
                styles.currentlyPlayingRibbon,
                'mobile-hidden'
              )}
            >
              <p
                className={classNames(
                  styles.provider,
                  styles.currentlyPlayingTitle
                )}
              >
                {t('currently_playing', { defaultValue: 'Currently Playing' })}
              </p>
              <button
                type="button"
                className={styles.ribbonFullscreenBtn}
                onClick={enterGameFullscreen}
                disabled={isGameLoading || !gameUrl || !iframeReady}
              >
                {t('enter_fullscreen', { defaultValue: 'Fullscreen' })}
              </button>
            </div>
            {currentGameTree}
          </>
        ) : null}
        {modern && !(isMobile && showGame) && (
          <ModernCasinoBrowser
            providers={providers}
            searching={searching}
            favorites={favoriteGames}
            tile={(game: CasinoGame) => tile(game)}
          />
        )}
        {classicRail && (
          <ClassicCasinoRail
            providers={providers}
            favorites={favoriteGames}
            tile={tile}
            byGameType={layout === 'vip' && useLiveCasino}
          />
        )}
        {!modern && !classicRail && !searching && favoriteGames.length > 0 && (
          <div
            className={classNames(
              styles.container,
              (gameUrl || isGameLoading) && 'mobile-hidden'
            )}
          >
            <div className={styles.providerWrapper}>
              <p className={styles.provider}>
                ⭐ {t('casino_favorites')}
                <span className={styles.providerCount}>
                  {favoriteGames.length}
                </span>
              </p>
            </div>
            <div className={styles.gameListContainer}>
              {favoriteGames.map((game) => tile(game))}
            </div>
          </div>
        )}
        {!modern &&
          !classicRail &&
          providers.map((providerGames: any, index: number) => {
            if (providerGames.games.length === 0) return <></>;
            return (
              <div
                key={index}
                className={classNames(
                  styles.container,
                  (gameUrl || isGameLoading) && 'mobile-hidden'
                )}
              >
                {providerGames.provider && providerGames.games?.length > 0 && (
                  <div
                    className={styles.providerWrapper}
                    onClick={() => changeExpanded(index)}
                  >
                    <p className={styles.provider}>
                      {casinoProviderLabel(providerGames.provider)}
                      <span className={styles.providerCount}>
                        {providerGames.games.length}
                      </span>
                    </p>
                    {!expanded[index] ? (
                      <div
                        className={classNames(styles.expandIcon, styles.icons)}
                      />
                    ) : (
                      <div
                        className={classNames(
                          styles.sizeDownIcon,
                          styles.icons
                        )}
                      />
                    )}
                  </div>
                )}
                <div
                  className={classNames(
                    styles.gameListContainer,
                    !expanded[index] ? ['mobile-hidden', 'desktop-hidden'] : ''
                  )}
                >
                  {providerGames.games.map((game: CasinoGame) =>
                    tile(game, true)
                  )}
                </div>
              </div>
            );
          })}
      </div>
      {isMobile && !showGame && !modern && !classicRail && (
        <div className={styles.popularGamesWrapper}>
          <button>
            {t('popular_games_label')}
            <div className={classNames('mobile-css-icons', styles.arrowIcon)} />
          </button>
        </div>
      )}
    </>
  );
};
